// src/portal/contexts/illustrations/components/shared/BookFavoriteButton.tsx
// Reusable Heart toggle for book detail / workspace headers.

'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Heart } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';
import { useIllustrationMutations } from '../../hooks/useBookMutations';
import { useToast } from '@/shared/components/ui/use-toast';

interface IllustrationFavoriteButtonProps {
  bookId: string;
  isFavorite: boolean;
  familyId?: string;
  /** Optional extra class names */
  className?: string;
}

export function IllustrationFavoriteButton({
  bookId,
  isFavorite,
  familyId,
  className,
}: IllustrationFavoriteButtonProps) {
  const t = useTranslations('illustrations.favorites');
  const { toast } = useToast();
  const { toggleFavorite } = useIllustrationMutations(familyId);
  const [pending, setPending] = useState(false);

  const handleClick = async () => {
    if (pending) return;
    setPending(true);
    try {
      const result = await toggleFavorite.mutateAsync(bookId);
      if (!result.success) {
        toast({ variant: 'destructive', description: result.error });
      }
    } catch {
      toast({ variant: 'destructive', description: t('addToFavorites') });
    } finally {
      setPending(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
      aria-pressed={isFavorite}
      data-testid={`illustration-favorite-toggle-${bookId}`}
      onClick={handleClick}
      disabled={pending}
      className={className}
    >
      <Heart
        className={cn(
          'h-5 w-5 transition-colors',
          isFavorite ? 'fill-primary text-primary' : 'text-muted-foreground',
        )}
      />
    </Button>
  );
}
